/**
 * FeedbackModal — admin-only capture of structured feedback on a Ion turn
 * or whole conversation. Driven by the chatStore `feedbackTarget` signal so
 * both the slide-over and the fullpage /assistant surface can open it without
 * prop drilling.
 *
 * Two polarities, one form:
 * - "Problem" (default, unchanged): category (Art, required) + area (Bereich,
 *   optional — "Automatic" lets the backend route by category) + severity +
 *   free-text comment.
 * - "Good": category becomes a positive-reason enum, severity is hidden, the
 *   comment field is relabelled "why was this good", and an "Add as
 *   evaluation template" checkbox (default on) sends `evalCandidate` — these
 *   rows become curation candidates for the AI eval suite
 *   (polarity='positive' AND evalCandidate, see backend IonFeedback model).
 *
 * The page-context snapshot is taken here at submit time (the targeted turn's
 * context isn't persisted server-side) and mapped onto the backend's
 * PageContext alias shape.
 *
 * Renders nothing for users without feedback access (not admin and no
 * feedback_access flag) or when no target is set.
 */

import { createPortal } from 'preact/compat'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { feedbackTarget, closeFeedback, currentConversationId } from '../../state/chatStore.js'
import { canGiveIonFeedback } from '../../state/userState.js'
import { getChatContextHint } from '../../modules/chatContextExtractor.js'
import { submitIonFeedback } from '../../services/ionConversationService.js'
import { t } from '../../i18n/index.js'

const CATEGORIES = [
  'wrong-data',
  'hallucination',
  'missing-feature',
  'bad-formatting',
  'ux',
  'other',
]
const POSITIVE_CATEGORIES = [
  'accurate',
  'well-explained',
  'good-format',
  'great-workflow',
  'other-good',
]
const AREAS = ['frontend-ux', 'backend-data', 'content-prompt'] // '' = automatic
const SEVERITIES = ['low', 'med', 'high']
const POLARITIES = ['problem', 'positive']

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_SCREENSHOT_BYTES = 6_000_000

/** Map the live contextHint to the backend PageContext alias shape. */
function hintToPageContext(hint) {
  if (!hint) return null
  return {
    page: hint.page,
    basis: hint.basis,
    domain: hint.domain,
    visibleCellIds: hint.visibleCellIds || [],
    compareCellIds: hint.compareCart || [],
    filterSummary: hint.filterSummary || null,
  }
}

// Exposed for unit tests (mirrors the tests/ion/ convention of testing
// pure helpers without pulling in @testing-library/preact).
export const __test__ = {
  hintToPageContext,
  CATEGORIES,
  POSITIVE_CATEGORIES,
  AREAS,
  SEVERITIES,
  POLARITIES,
}

export function FeedbackModal() {
  const target = feedbackTarget.value
  const [location] = useLocation()
  const [polarity, setPolarity] = useState('problem')
  const [category, setCategory] = useState('wrong-data')
  const [area, setArea] = useState('') // '' = automatic
  const [severity, setSeverity] = useState('med')
  const [comment, setComment] = useState('')
  const [evalCandidate, setEvalCandidate] = useState(true)
  const [screenshot, setScreenshot] = useState(null) // data URL | null
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  // Reset the form whenever a new target opens the modal.
  useEffect(() => {
    if (target) {
      setPolarity('problem')
      setCategory('wrong-data')
      setArea('')
      setSeverity('med')
      setComment('')
      setEvalCandidate(true)
      setScreenshot(null)
      setError(null)
      setSubmitting(false)
    }
  }, [target])

  // Switching polarity swaps the category to that polarity's first (default)
  // option — a "positive" category value is never valid for 'problem' and
  // vice versa (the backend rejects the mismatch).
  function handlePolarityChange(next) {
    setPolarity(next)
    setCategory(next === 'positive' ? POSITIVE_CATEGORIES[0] : CATEGORIES[0])
  }

  // Esc closes.
  useEffect(() => {
    if (!target) return
    function onKey(e) {
      if (e.key === 'Escape') closeFeedback()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target])

  function ingestImageFile(file) {
    if (!file) return
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t('ion.feedback.screenshot.bad_type'))
      return
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError(t('ion.feedback.screenshot.too_large'))
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setScreenshot(reader.result) // data URL
      setError(null)
    }
    // Without this a failed read was completely silent: no screenshot, no
    // message, and a reviewer convinced they had attached one.
    reader.onerror = () => {
      setScreenshot(null)
      setError(t('ion.feedback.screenshot.read_failed'))
    }
    reader.readAsDataURL(file)
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items || []
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile()
        if (file) {
          e.preventDefault()
          ingestImageFile(file)
          return
        }
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer?.files?.[0]
    if (file) ingestImageFile(file)
  }

  if (!target || !canGiveIonFeedback()) return null
  if (typeof document === 'undefined') return null

  const conversationId = currentConversationId.value
  const titleKey =
    target.scope === 'message'
      ? 'ion.feedback.title.message'
      : 'ion.feedback.title.conversation'

  async function handleSubmit(e) {
    e.preventDefault()
    if (!conversationId) {
      setError(t('ion.feedback.no_conversation'))
      return
    }
    if (!comment.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await submitIonFeedback({
        conversationId,
        scope: target.scope,
        targetTurnIndex: target.scope === 'message' ? target.targetTurnIndex : null,
        polarity,
        category,
        area: polarity === 'positive' ? null : area || null,
        severity,
        comment: comment.trim(),
        pageContext: hintToPageContext(getChatContextHint(location)),
        screenshot: screenshot || null,
        evalCandidate: polarity === 'positive' ? evalCandidate : false,
      })
      closeFeedback()
    } catch (err) {
      setError(t('ion.feedback.error'))
      setSubmitting(false)
    }
  }

  return createPortal(
    <div class="ion-fb-overlay" onClick={closeFeedback}>
      <div
        class="ion-fb-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t(titleKey)}
        onClick={(e) => e.stopPropagation()}
        onPaste={handlePaste}
      >
        <header class="ion-fb-modal__header">
          <h3 class="ion-fb-modal__title">{t(titleKey)}</h3>
          <button
            type="button"
            class="ion-fb-modal__close"
            onClick={closeFeedback}
            aria-label={t('ion.feedback.cancel')}
          >
            ×
          </button>
        </header>

        <form class="ion-fb-modal__body" onSubmit={handleSubmit}>
          <div class="ion-fb-field">
            <span class="ion-fb-field__label">{t('ion.feedback.field.polarity')}</span>
            <div class="ion-fb-segmented">
              {POLARITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  class={`ion-fb-seg ${polarity === p ? 'is-active' : ''}`}
                  onClick={() => handlePolarityChange(p)}
                >
                  {t(`ion.feedback.polarity.${p}`)}
                </button>
              ))}
            </div>
          </div>

          <label class="ion-fb-field">
            <span class="ion-fb-field__label">
              {t(polarity === 'positive' ? 'ion.feedback.field.reason' : 'ion.feedback.field.category')}
            </span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {(polarity === 'positive' ? POSITIVE_CATEGORIES : CATEGORIES).map((c) => (
                <option key={c} value={c}>
                  {t(`ion.feedback.category.${c}`)}
                </option>
              ))}
            </select>
          </label>

          {polarity === 'problem' && (
            <label class="ion-fb-field">
              <span class="ion-fb-field__label">{t('ion.feedback.field.area')}</span>
              <select value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">{t('ion.feedback.area.auto')}</option>
                {AREAS.map((a) => (
                  <option key={a} value={a}>
                    {t(`ion.feedback.area.${a}`)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {polarity === 'problem' && (
            <div class="ion-fb-field">
              <span class="ion-fb-field__label">{t('ion.feedback.field.severity')}</span>
              <div class="ion-fb-segmented">
                {SEVERITIES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    class={`ion-fb-seg ${severity === s ? 'is-active' : ''}`}
                    onClick={() => setSeverity(s)}
                  >
                    {t(`ion.feedback.severity.${s}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label class="ion-fb-field">
            <span class="ion-fb-field__label">
              {t(polarity === 'positive' ? 'ion.feedback.field.comment_positive' : 'ion.feedback.field.comment')}
            </span>
            <textarea
              value={comment}
              maxLength={4000}
              rows={5}
              placeholder={t(
                polarity === 'positive'
                  ? 'ion.feedback.comment.placeholder_positive'
                  : 'ion.feedback.comment.placeholder'
              )}
              onInput={(e) => setComment(e.target.value)}
              autoFocus
            />
          </label>

          {polarity === 'positive' && (
            <label class="ion-fb-field ion-fb-field--checkbox">
              <input
                type="checkbox"
                checked={evalCandidate}
                onChange={(e) => setEvalCandidate(e.target.checked)}
              />
              <span class="ion-fb-field__label">{t('ion.feedback.field.eval_candidate')}</span>
            </label>
          )}

          <div class="ion-fb-field">
            <span class="ion-fb-field__label">{t('ion.feedback.field.screenshot')}</span>
            {!screenshot && (category === 'bad-formatting' || category === 'ux') && (
              <p class="ion-fb-nudge">{t('ion.feedback.screenshot.nudge')}</p>
            )}
            {screenshot ? (
              <div class="ion-fb-shot">
                <img class="ion-fb-shot__img" src={screenshot} alt="screenshot preview" />
                <button
                  type="button"
                  class="ion-fb-shot__remove"
                  onClick={() => setScreenshot(null)}
                >
                  {t('ion.feedback.screenshot.remove')}
                </button>
              </div>
            ) : (
              // Passive paste/drop target — clicking it must NOT open the OS
              // file picker (that surprised users mid-paste). Picking a file
              // is an explicit button instead.
              <div
                class="ion-fb-dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <span class="ion-fb-dropzone__hint">
                  {t('ion.feedback.screenshot.hint')}
                </span>
                <button
                  type="button"
                  class="ion-fb-dropzone__pick"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('ion.feedback.screenshot.choose')}
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => ingestImageFile(e.target.files?.[0])}
            />
          </div>

          {error && <div class="ion-fb-error" role="alert">{error}</div>}

          <footer class="ion-fb-modal__footer">
            <button
              type="button"
              class="ion-fb-btn ion-fb-btn--ghost"
              onClick={closeFeedback}
              disabled={submitting}
            >
              {t('ion.feedback.cancel')}
            </button>
            <button
              type="submit"
              class="ion-fb-btn ion-fb-btn--primary"
              disabled={submitting || !comment.trim()}
            >
              {submitting ? t('ion.feedback.submitting') : t('ion.feedback.submit')}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  )
}
